// src/context/auth/AuthContext.tsx - OPTIMIZED VERSION
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import {
  setFirebaseChatUser,
  clearFirebaseChatUser,
  initializeFirebaseChatUser,
  syncFirebaseUserProfile,          // ← NEW
} from '../../utils/authHelper';
import { clearTokens } from '../../Component/api/tokenStore';
import { getMyProfile } from '../../services/auth';

// =============================================================================
// CACHE VERSION — bump this number whenever the API response shape changes.
// This invalidates any stale localStorage data automatically on next load,
// preventing old cached fields from silently overriding the new response shape.
// OLD shape = "v1" | NEW shape = "v2"
// v5: wipes stale admin caches whose `permissions` may differ from the latest
// backend role definition (e.g. chat revoked but still cached as granted).
const USER_CACHE_VERSION = "v5";
const USER_CACHE_KEY = "user";
const USER_CACHE_VERSION_KEY = "user_cache_version";
// =============================================================================

// =============================================================================
// USER INTERFACE
// =============================================================================

// OLD RESPONSE — comment this block + uncomment NEW block to switch
// interface User {
//   nutrist_id: number;
//   nutrist_email: string;
//   nutrist_fname?: string;
//   nutrist_lname?: string;
//   nutrist_title?: string;
//   nutrist_assign_role?: string;
//   nutrist_profile?: string;
//   // Extended profile fields
//   nutrist_mobileno?: string;
//   nutrist_working?: string;
//   nutrist_rating?: string;
//   nutrist_dob?: string;
//   nutrist_gender?: string;
//   nutrist_address?: string;
//   nutrist_state?: string;
//   nutrist_city?: string;
//   nutrist_pincode?: string;
//   nutrist_job_type?: string;
//   nutrist_workexperience?: string;
//   nutrist_lang1?: string;
//   nutrist_lang2?: string;
//   nutrist_lang3?: string;
//   nutrist_lang4?: string;
//   nutrist_joining_date?: string;
//   nutrist_status?: string;
//   specialisation_details?: Array<{
//     spec_id: number;
//     specialisation: string;
//     experience: string;
//   }>;
//   expertise_details?: Array<{
//     expertise_id: number;
//     expertise: string;
//   }>;
// }

// NEW RESPONSE — comment this block + uncomment OLD block to revert
// Changes vs OLD:
//   - specialisation_details: { spec_id, specialisation, experience } → { spec_exp, specialisation }
//   - expertise_details:      { expertise_id, expertise }            → { expertise }
//   - Added: nutrist_education_details, nutrist_document_details,
//            nutrist_certification, nutrist_interest_zone,
//            nutrist_reporting_manager, nutrist_leaves_taken, assigned_users
interface User {
  nutrist_id: number;
  nutrist_email: string;
  nutrist_fname?: string;
  nutrist_lname?: string;
  nutrist_title?: string;
  nutrist_assign_role?: string;
  nutrist_profile?: string;
  // Extended profile fields
  nutrist_mobileno?: string;
  nutrist_working?: string;
  nutrist_rating?: string;
  nutrist_dob?: string;
  nutrist_gender?: string;
  nutrist_address?: string;
  nutrist_state?: string;
  nutrist_city?: string;
  nutrist_pincode?: string;
  nutrist_job_type?: string;
  nutrist_workexperience?: string;
  nutrist_lang1?: string;
  nutrist_lang2?: string;
  nutrist_lang3?: string;
  nutrist_lang4?: string;
  nutrist_joining_date?: string;
  nutrist_status?: string;
  // NEW: shape changed from { spec_id, specialisation, experience }
  specialisation_details?: Array<{
    spec_exp: string;
    specialisation: string;
  }>;
  // NEW: shape changed from { expertise_id, expertise }
  expertise_details?: Array<{
    expertise: string;
  }>;
  // NEW fields not present in old response
  nutrist_education_details?: Array<{
    education_clg_name: string;
    education_qualification: string;
  }>;
  nutrist_document_details?: Array<{
    doc_no: string;
    doc_type: string;
    doc_upload_url: string | null;
  }>;
  nutrist_certification?: Array<{
    certification_name: string;
  }>;
  nutrist_interest_zone?: Array<{
    interest_zone: string;
  }>;
  nutrist_reporting_manager?: string;
  nutrist_leaves_taken?: string;
  assigned_users?: Array<{
    user_id: number;
    user_name: string;
    subscription_plan_name: string;
    assigned_date: string;
    followup_date: string;
    users_rating: string;
    user_profile: string;
  }>;
  // Returned by VERIFY_OTP (and propagated through the User object so UI can gate on it)
  role?: string;
  permissions?: Record<string, Record<string, boolean>>;
}
// =============================================================================

interface AuthContextType {
  user: User | null;
  login: (userData: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  updateUserProfile: (profileData: any) => void;
  refreshProfile: () => Promise<void>;
  isLoadingProfile: boolean;
  isLoading: boolean;
  // True only while a window-focus-triggered profile refresh is in flight.
  // Used by ProtectedRoute to dim the UI on tab re-activation — NOT set on
  // cold page load, login, or background mount refreshes.
  isRefreshingFromFocus: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper: save user + stamp the current cache version
const saveUserToStorage = (userData: User) => {
  localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
  localStorage.setItem(USER_CACHE_VERSION_KEY, USER_CACHE_VERSION);
};

// Helper: clear user + version stamp
const clearUserFromStorage = () => {
  localStorage.removeItem(USER_CACHE_KEY);
  localStorage.removeItem(USER_CACHE_VERSION_KEY);
};

// Helper: returns parsed user only if cache version matches, otherwise clears stale data
const loadUserFromStorage = (): User | null => {
  const storedVersion = localStorage.getItem(USER_CACHE_VERSION_KEY);
  if (storedVersion !== USER_CACHE_VERSION) {
    // Cache is stale — wipe it so a fresh fetch happens
    console.log(`AuthContext: Cache version mismatch (stored=${storedVersion}, current=${USER_CACHE_VERSION}). Clearing stale user data.`);
    clearUserFromStorage();
    return null;
  }
  const storedUser = localStorage.getItem(USER_CACHE_KEY);
  if (!storedUser) return null;
  try {
    return JSON.parse(storedUser) as User;
  } catch {
    clearUserFromStorage();
    return null;
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingFromFocus, setIsRefreshingFromFocus] = useState(false);

  // Fetch and update profile data (plus role + permissions for RBAC).
  // GET_PROFILE (/get_my_profile/) is the single source of truth for the
  // logged-in user: the access token identifies the nutritionist, so no id
  // is passed. Response carries both the full profile and the live
  // permissions map.
  const fetchAndUpdateProfile = useCallback(async (_nutristId: number) => {
    setIsLoadingProfile(true);
    try {
      // Service handles success-flag inspection + payload extraction (legacy
      // `nutritionist_data` vs new `data` shapes) and throws ApiError on failure.
      const nutritionistPayload = await getMyProfile();

      // =========================================================================
      // OLD RESPONSE — comment this block + uncomment NEW block to switch
      // Root key was: data.nutritionists[0]
      // =========================================================================
      // if (data.success === "1" && data.nutritionists && data.nutritionists.length > 0) {
      //   const nutritionist = data.nutritionists[0];
      //
      //   const fullUserData: User = {
      //     nutrist_id: nutritionist.nutrist_id,
      //     nutrist_email: nutritionist.nutrist_email,
      //     nutrist_fname: nutritionist.nutrist_fname,
      //     nutrist_lname: nutritionist.nutrist_lname,
      //     nutrist_title: nutritionist.nutrist_title,
      //     nutrist_assign_role: nutritionist.nutrist_assign_role,
      //     nutrist_profile: nutritionist.nutrist_profile,
      //     nutrist_mobileno: nutritionist.nutrist_mobileno,
      //     nutrist_working: nutritionist.nutrist_working,
      //     nutrist_rating: nutritionist.nutrist_rating,
      //     nutrist_dob: nutritionist.nutrist_dob,
      //     nutrist_gender: nutritionist.nutrist_gender,
      //     nutrist_address: nutritionist.nutrist_address,
      //     nutrist_state: nutritionist.nutrist_state,
      //     nutrist_city: nutritionist.nutrist_city,
      //     nutrist_pincode: nutritionist.nutrist_pincode,
      //     nutrist_job_type: nutritionist.nutrist_job_type,
      //     nutrist_workexperience: nutritionist.nutrist_workexperience,
      //     nutrist_lang1: nutritionist.nutrist_lang1,
      //     nutrist_lang2: nutritionist.nutrist_lang2,
      //     nutrist_lang3: nutritionist.nutrist_lang3,
      //     nutrist_lang4: nutritionist.nutrist_lang4,
      //     nutrist_joining_date: nutritionist.nutrist_joining_date,
      //     nutrist_status: nutritionist.nutrist_status,
      //     specialisation_details: nutritionist.specialisation_details,  // { spec_id, specialisation, experience }
      //     expertise_details: nutritionist.expertise_details,            // { expertise_id, expertise }
      //   };
      //
      //   setUser(fullUserData);
      //   saveUserToStorage(fullUserData);
      //   initializeFirebaseChatUser(fullUserData);
      //   syncFirebaseUserProfile(fullUserData);  // ← sync Firebase doc
      // }
      // =========================================================================

      // NEW RESPONSE — comment this block + uncomment OLD block to revert
      // Root key may be `data` (GET_PROFILE / GET_NUTRITIONIST_FULL_DETAILS) or `nutritionist_data` (legacy).
      {
        const nutritionist = nutritionistPayload;

        const fullUserData: User = {
          nutrist_id: nutritionist.nutrist_id,
          nutrist_email: nutritionist.nutrist_email,
          nutrist_fname: nutritionist.nutrist_fname,
          nutrist_lname: nutritionist.nutrist_lname,
          nutrist_title: nutritionist.nutrist_title,
          nutrist_assign_role: nutritionist.nutrist_assign_role,
          nutrist_profile: nutritionist.nutrist_profile,
          nutrist_mobileno: nutritionist.nutrist_mobileno,
          nutrist_working: nutritionist.nutrist_working,
          nutrist_rating: nutritionist.nutrist_rating,
          nutrist_dob: nutritionist.nutrist_dob,
          nutrist_gender: nutritionist.nutrist_gender,
          nutrist_address: nutritionist.nutrist_address,
          nutrist_state: nutritionist.nutrist_state,
          nutrist_city: nutritionist.nutrist_city,
          nutrist_pincode: nutritionist.nutrist_pincode,
          nutrist_job_type: nutritionist.nutrist_job_type,
          nutrist_workexperience: nutritionist.nutrist_workexperience,
          nutrist_lang1: nutritionist.nutrist_lang1,
          nutrist_lang2: nutritionist.nutrist_lang2,
          nutrist_lang3: nutritionist.nutrist_lang3,
          nutrist_lang4: nutritionist.nutrist_lang4,
          nutrist_joining_date: nutritionist.nutrist_joining_date,
          nutrist_status: nutritionist.nutrist_status,
          // NEW: key renamed + shape changed
          specialisation_details: nutritionist.nutrist_specialisation_details, // { spec_exp, specialisation }
          expertise_details: nutritionist.nutrist_expertise_details,           // { expertise }
          // NEW fields
          nutrist_education_details: nutritionist.nutrist_education_details,
          nutrist_document_details: nutritionist.nutrist_document_details,
          nutrist_certification: nutritionist.nutrist_certification,
          nutrist_interest_zone: nutritionist.nutrist_interest_zone,
          nutrist_reporting_manager: nutritionist.nutrist_reporting_manager,
          nutrist_leaves_taken: nutritionist.nutrist_leaves_taken,
          assigned_users: nutritionist.assigned_users,
        };

        // Only preserve cached role/permissions when the response object does NOT
        // carry the field at all. If the backend explicitly returns the key — even
        // as null or an empty object — that fresh value is authoritative. Without
        // this distinction, a stale cached `permissions` would silently override
        // a backend revocation (e.g. admin's chat access being turned off).
        const hasFreshRole = Object.prototype.hasOwnProperty.call(nutritionist, 'role');
        const hasFreshPermissions = Object.prototype.hasOwnProperty.call(nutritionist, 'permissions');
        setUser((prev) => {
          const merged: User = {
            ...fullUserData,
            role: hasFreshRole ? nutritionist.role : prev?.role,
            permissions: hasFreshPermissions ? nutritionist.permissions : prev?.permissions,
          };
          saveUserToStorage(merged);
          initializeFirebaseChatUser(merged);
          syncFirebaseUserProfile(merged);  // ← NEW: sync Firebase doc with latest API data
          return merged;
        });
      }
      // =========================================================================

    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  }, []);

  // Load user from localStorage on mount
  useEffect(() => {
    // loadUserFromStorage() automatically clears stale cache if version doesn't match
    const parsedUser = loadUserFromStorage();

    if (parsedUser) {
      setUser(parsedUser);
      initializeFirebaseChatUser(parsedUser);

      // Always re-fetch to get latest data (cache is only used for instant render)
      if (parsedUser.nutrist_id) {
        console.log('AuthContext: Using cached profile for instant render, refreshing in background...');
        fetchAndUpdateProfile(parsedUser.nutrist_id);
      }
    }

    setIsLoading(false);
  }, [fetchAndUpdateProfile]);

  // Listen for session-expired events emitted by apiFetch when a 401 refresh
  // cycle ultimately fails. Clearing user state here flips `isAuthenticated`
  // to false, which causes ProtectedRoute to render a client-side <Navigate>
  // to /signin — no full page reload. Tokens and localStorage user cache are
  // already wiped by apiFetch before the event fires; we just need to sync
  // React state and Firebase.
  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      clearFirebaseChatUser();
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  // Refresh profile + permissions when the browser tab regains focus.
  // 30s cooldown prevents spam from rapid tab-switching.
  // isRefreshingFromFocus is flipped ONLY here so the UI can show a dimmed
  // overlay during tab re-activation, while cold loads stay overlay-free.
  const lastFocusFetchRef = useRef<number>(0);
  const FOCUS_REFRESH_COOLDOWN_MS = 30_000;
  useEffect(() => {
    if (!user?.nutrist_id) return;
    const nutristId = user.nutrist_id;
    const handleFocus = async () => {
      const now = Date.now();
      if (now - lastFocusFetchRef.current < FOCUS_REFRESH_COOLDOWN_MS) return;
      lastFocusFetchRef.current = now;
      setIsRefreshingFromFocus(true);
      try {
        await fetchAndUpdateProfile(nutristId);
      } finally {
        setIsRefreshingFromFocus(false);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.nutrist_id, fetchAndUpdateProfile]);

  const login = (userData: User) => {
    setUser(userData);
    saveUserToStorage(userData);
    initializeFirebaseChatUser(userData);
    syncFirebaseUserProfile(userData);  // ← NEW: create/update Firebase doc on login

    // Fetch full profile data after login
    if (userData.nutrist_id) {
      fetchAndUpdateProfile(userData.nutrist_id);
    }
  };

  const logout = () => {
    setUser(null);
    clearUserFromStorage();
    clearTokens();
    clearFirebaseChatUser();
  };

  const updateUserProfile = useCallback((profileData: any) => {
    // =========================================================================
    // OLD RESPONSE — comment this block + uncomment NEW block to switch
    // =========================================================================
    // const updatedUser: User = {
    //   nutrist_id: profileData.nutrist_id,
    //   nutrist_email: profileData.nutrist_email,
    //   nutrist_fname: profileData.nutrist_fname,
    //   nutrist_lname: profileData.nutrist_lname,
    //   nutrist_title: profileData.nutrist_title,
    //   nutrist_assign_role: profileData.nutrist_assign_role,
    //   nutrist_profile: profileData.nutrist_profile,
    //   nutrist_mobileno: profileData.nutrist_mobileno,
    //   nutrist_working: profileData.nutrist_working,
    //   nutrist_rating: profileData.nutrist_rating,
    //   nutrist_dob: profileData.nutrist_dob,
    //   nutrist_gender: profileData.nutrist_gender,
    //   nutrist_address: profileData.nutrist_address,
    //   nutrist_state: profileData.nutrist_state,
    //   nutrist_city: profileData.nutrist_city,
    //   nutrist_pincode: profileData.nutrist_pincode,
    //   nutrist_job_type: profileData.nutrist_job_type,
    //   nutrist_workexperience: profileData.nutrist_workexperience,
    //   nutrist_lang1: profileData.nutrist_lang1,
    //   nutrist_lang2: profileData.nutrist_lang2,
    //   nutrist_lang3: profileData.nutrist_lang3,
    //   nutrist_lang4: profileData.nutrist_lang4,
    //   nutrist_joining_date: profileData.nutrist_joining_date,
    //   nutrist_status: profileData.nutrist_status,
    //   specialisation_details: profileData.specialisation_details,  // { spec_id, specialisation, experience }
    //   expertise_details: profileData.expertise_details,            // { expertise_id, expertise }
    // };
    //
    // setUser(updatedUser);
    // saveUserToStorage(updatedUser);
    // initializeFirebaseChatUser(updatedUser);
    // syncFirebaseUserProfile(updatedUser);  // ← sync Firebase doc
    // =========================================================================

    // NEW RESPONSE — comment this block + uncomment OLD block to revert
    const updatedUser: User = {
      nutrist_id: profileData.nutrist_id,
      nutrist_email: profileData.nutrist_email,
      nutrist_fname: profileData.nutrist_fname,
      nutrist_lname: profileData.nutrist_lname,
      nutrist_title: profileData.nutrist_title,
      nutrist_assign_role: profileData.nutrist_assign_role,
      nutrist_profile: profileData.nutrist_profile,
      nutrist_mobileno: profileData.nutrist_mobileno,
      nutrist_working: profileData.nutrist_working,
      nutrist_rating: profileData.nutrist_rating,
      nutrist_dob: profileData.nutrist_dob,
      nutrist_gender: profileData.nutrist_gender,
      nutrist_address: profileData.nutrist_address,
      nutrist_state: profileData.nutrist_state,
      nutrist_city: profileData.nutrist_city,
      nutrist_pincode: profileData.nutrist_pincode,
      nutrist_job_type: profileData.nutrist_job_type,
      nutrist_workexperience: profileData.nutrist_workexperience,
      nutrist_lang1: profileData.nutrist_lang1,
      nutrist_lang2: profileData.nutrist_lang2,
      nutrist_lang3: profileData.nutrist_lang3,
      nutrist_lang4: profileData.nutrist_lang4,
      nutrist_joining_date: profileData.nutrist_joining_date,
      nutrist_status: profileData.nutrist_status,
      // NEW: key renamed + shape changed
      specialisation_details: profileData.nutrist_specialisation_details, // { spec_exp, specialisation }
      expertise_details: profileData.nutrist_expertise_details,           // { expertise }
      // NEW fields
      nutrist_education_details: profileData.nutrist_education_details,
      nutrist_document_details: profileData.nutrist_document_details,
      nutrist_certification: profileData.nutrist_certification,
      nutrist_interest_zone: profileData.nutrist_interest_zone,
      nutrist_reporting_manager: profileData.nutrist_reporting_manager,
      nutrist_leaves_taken: profileData.nutrist_leaves_taken,
      assigned_users: profileData.assigned_users,
    };
    // =========================================================================

    setUser(updatedUser);
    saveUserToStorage(updatedUser);
    initializeFirebaseChatUser(updatedUser);
    syncFirebaseUserProfile(updatedUser);  // ← NEW: sync Firebase doc on profile update
  }, []);

  // Manual refresh function for profile updates
  const refreshProfile = useCallback(async () => {
    if (user?.nutrist_id) {
      await fetchAndUpdateProfile(user.nutrist_id);
    }
  }, [user?.nutrist_id, fetchAndUpdateProfile]);

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    updateUserProfile,
    refreshProfile,
    isLoadingProfile,
    isLoading,
    isRefreshingFromFocus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};